"use client";

import React from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const applications = [{
  id: 1,
  title: "Chiropractic Adjustments",
  description: "Restore your body's natural alignment with expert spinal care. Our Limassol and Nicosia clinics provide precise adjustments to alleviate pain, improve mobility, and enhance your overall nervous system function.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609985/img/luxury_penthouse_stone_application.png",
  colSpan: "lg:col-span-7 lg:col-start-1",
  aspectRatio: 16 / 10,
  delay: "delay-0"
}, {
  id: 2,
  title: "Clinical Excellence",
  description: "Our multi-disciplinary approach combines modern diagnostics with warm, personalized care. We create a welcoming environment where both local residents and international expats feel right at home while receiving world-class treatment.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609985/img/corporate_lobby_stone_application.png",
  colSpan: "lg:col-span-4 lg:col-start-9 lg:mt-48",
  aspectRatio: 3 / 4,
  delay: "delay-150"
}, {
  id: 3,
  title: "Acupuncture & Recovery",
  description: "Experience the healing power of dry needling and acupuncture. These therapeutic treatments target deep-seated tension and promote long-term recovery, perfectly integrated with our holistic wellness philosophy.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609985/img/spa_retreat_stone_application.png",
  colSpan: "lg:col-span-8 lg:col-start-3 lg:mt-24",
  aspectRatio: 21 / 9,
  delay: "delay-300"
}];
function AnimatedCard({
  item,
  index
}: {
  item: (typeof applications)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px"
  });
  return <div data-section-id="152282" data-index={index} ref={ref} className={`col-span-1 md:col-span-12 ${item.colSpan} transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${item.delay}`}>
      <Card className="group h-full overflow-hidden rounded-2xl border-border/40 bg-white/70 shadow-lg backdrop-blur-md transition-all duration-500 hover:border-accent/30 hover:shadow-2xl dark:bg-card/20">
        <div className="relative overflow-hidden">
          <AspectRatio ratio={item.aspectRatio}>
            <img src={item.image} alt={item.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          </AspectRatio>
        </div>
        <CardHeader className="pt-8 pb-4">
          <div className="mb-4 flex items-center gap-2 text-accent">
            <Sparkles className="size-4" />
            <span className="text-xs font-sans font-semibold uppercase tracking-widest">
              Bodywerks Expertise
            </span>
          </div>
          <CardTitle className="font-serif text-3xl font-medium tracking-wide text-foreground md:text-4xl">
            {item.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="font-sans text-base leading-relaxed text-secondary-foreground/80 md:text-lg">
            {item.description}
          </CardDescription>
        </CardContent>
        <CardFooter className="pb-8 pt-4">
          <Button asChild variant="ghost" className="group/btn -ml-4 px-4 font-sans text-accent hover:bg-transparent hover:text-accent/80">
            <Link to="/TreatmentsPage">
              Learn More
              <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>;
}
export default function ArchitecturalApplications() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="imagehero" className="relative w-full overflow-hidden bg-background py-24 md:py-32 lg:py-40">
      <div className="pointer-events-none absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-border to-transparent opacity-30" />
      <div className="pointer-events-none absolute top-0 left-1/4 h-full w-px bg-gradient-to-b from-transparent via-border to-transparent opacity-5" />
      <div className="pointer-events-none absolute top-0 right-1/4 h-full w-px bg-gradient-to-b from-transparent via-border to-transparent opacity-5" />

      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mb-16 max-w-3xl transition-all duration-1000 ease-out md:mb-24 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl font-medium leading-tight tracking-wide text-foreground md:text-5xl lg:text-6xl">
            Elevated Care
          </h2>
          <Separator className="mt-8 mb-6 w-24 bg-accent/30" />
          <p className="font-sans text-lg leading-relaxed text-secondary-foreground/70 md:text-xl">
            Experience a multi-disciplinary approach to health at Bodywerks. Our clinics in Limassol and Nicosia offer professional chiropractic and acupuncture services tailored to your body's unique needs.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12 lg:gap-8">
          {applications.map((item, index) => <AnimatedCard key={item.id} item={item} index={index} />)}
        </div>
      </div>
    </section>;
}